// Whether a failed upload is worth sending again.
//
// The offline sync engine retries a failed photo upload on every sync pass,
// and a manual "Sync Now" clears the attempt budget outright. That is right
// for a dropped connection and wrong for a body the server has judged: the
// same bytes re-sent get the same answer, so an unclassified rejection
// becomes an unbounded retry loop against a request that can never succeed.

/**
 * A 4xx other than the two that explicitly invite a retry (408 Request
 * Timeout, 429 Too Many Requests) is the server's verdict on this request,
 * not on its timing. 5xx and transport failures stay retryable.
 */
const isPermanentStatus = (status: number): boolean =>
  status >= 400 && status < 500 && status !== 408 && status !== 429;

/**
 * Tagged failures the upload endpoints declare. A status the contract names is
 * never a `ResponseError` — the derived client hands back the tagged value
 * instead — so matching on the response status alone misses exactly the
 * refusals the server states most clearly. Each of these is a verdict on the
 * request: the caller may not upload here, or the destination does not exist.
 */
const DECLARED_TERMINAL_TAGS: ReadonlySet<string> = new Set([
  "Forbidden",
  "PublicFolderNotConfigured",
]);

/**
 * The derived client fails with `HttpClientError.ResponseError` for any
 * status the endpoint does not declare, carrying the response it read.
 */
export const isPermanentUploadRejection = (error: unknown): boolean => {
  if (typeof error !== "object" || error === null) return false;
  const candidate = error as {
    readonly _tag?: unknown;
    readonly status?: unknown;
    readonly response?: { readonly status?: unknown };
  };
  if (
    typeof candidate._tag === "string" &&
    DECLARED_TERMINAL_TAGS.has(candidate._tag)
  ) {
    return true;
  }
  // A failure the api boundary has already read carries the status itself.
  if (typeof candidate.status === "number") {
    return isPermanentStatus(candidate.status);
  }
  if (candidate._tag !== "ResponseError") return false;
  const status = candidate.response?.status;
  return typeof status === "number" && isPermanentStatus(status);
};

/** Recorded on the photo so a later pass can tell a verdict from an outage. */
export const PERMANENT_UPLOAD_REJECTION =
  "The server rejected this photo. Please remove it and retake or re-upload it.";
